import { useState, useRef } from 'react'
import {
  Upload,
  Download,
  FileSpreadsheet,
  CheckCircle2,
  AlertTriangle,
  X,
  Table,
} from 'lucide-react'
import ExcelJS from 'exceljs'
import type { UnderwritingModel } from '@/types/underwritingModels'

interface Props {
  model: UnderwritingModel
  dealId: string
  values: Record<string, number | string>
  onImport: (data: Record<string, number | string>) => void
}

interface ParsedRow {
  field: string
  value: string | number
  matched: boolean
  fieldId?: string
}

export default function SpreadsheetImportExport({ model, dealId, values, onImport }: Props) {
  const [importData, setImportData] = useState<ParsedRow[] | null>(null)
  const [importError, setImportError] = useState<string | null>(null)
  const [showPreview, setShowPreview] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // All field IDs and labels for matching
  const allFields = model.sections.flatMap((s) => s.fields)
  const fieldMap = new Map(allFields.map((f) => [f.label.toLowerCase(), f.id]))
  const fieldIdMap = new Map(allFields.map((f) => [f.id, f.label]))

  /* ---- Export to XLSX (ExcelJS) ---- */
  const handleExport = async () => {
    const wb = new ExcelJS.Workbook()
    wb.creator = 'CapitalMarketOS'
    wb.created = new Date()

    // Data sheet
    const ws = wb.addWorksheet(model.assetClass)
    ws.columns = [
      { header: 'Section',   key: 'Section',   width: 26 },
      { header: 'Field',     key: 'Field',     width: 34 },
      { header: 'Field ID',  key: 'Field ID',  width: 26 },
      { header: 'Value',     key: 'Value',     width: 20 },
      { header: 'Type',      key: 'Type',      width: 14 },
    ]

    // Bold header row
    const headerRow = ws.getRow(1)
    headerRow.font = { bold: true }
    headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1A1A1A' } }
    headerRow.commit()

    model.sections.forEach((section) => {
      section.fields.forEach((field) => {
        ws.addRow({
          Section: section.label,
          Field: field.label,
          'Field ID': field.id,
          Value: values[field.id] ?? (field.defaultValue ?? ''),
          Type: field.type,
        })
      })
    })

    // Instructions sheet
    const instrWs = wb.addWorksheet('Instructions')
    instrWs.columns = [{ header: 'Instruction', key: 'Instruction', width: 70 }]
    const instrHeader = instrWs.getRow(1)
    instrHeader.font = { bold: true }
    instrHeader.commit()

    const instrRows = [
      'HOW TO USE THIS TEMPLATE',
      '',
      '1. Fill in the "Value" column on the first sheet with your data.',
      '2. Do not modify the "Field ID" column — it is used for re-import matching.',
      '3. Save the file and use "Import from Spreadsheet" to load data back.',
      '4. Calculated fields will be auto-computed on import.',
      '',
      `Asset Class: ${model.assetClass}`,
      `Model Version: v${model.version}`,
      `Deal: ${dealId}`,
      `Exported: ${new Date().toISOString().slice(0, 19)}`,
    ]
    instrRows.forEach((text) => instrWs.addRow({ Instruction: text }))

    // Write to buffer and trigger download
    const buffer = await wb.xlsx.writeBuffer()
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `UW_${model.assetClass.replace(/\s+/g, '_')}_${dealId}_${new Date().toISOString().slice(0, 10)}.xlsx`
    a.click()
    URL.revokeObjectURL(url)
  }

  /* ---- Import from XLSX (ExcelJS) ---- */
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setImportError(null)
    setImportData(null)

    const reader = new FileReader()
    reader.onload = async (evt) => {
      try {
        const buffer = evt.target?.result as ArrayBuffer
        if (!buffer) {
          setImportError('Could not read file.')
          return
        }

        const wb = new ExcelJS.Workbook()
        await wb.xlsx.load(buffer)

        const ws = wb.worksheets[0]
        if (!ws) {
          setImportError('No worksheet found in the file.')
          return
        }

        // Read header row to find column indices
        const headerRow = ws.getRow(1)
        const headers: Record<string, number> = {}
        headerRow.eachCell((cell, colNum) => {
          headers[String(cell.value)] = colNum
        })

        const sectionCol  = headers['Section']
        const fieldCol    = headers['Field']
        const fieldIdCol  = headers['Field ID']
        const valueCol    = headers['Value']

        if (!fieldCol && !fieldIdCol) {
          setImportError('Could not match any fields. Ensure the spreadsheet has "Field ID" or "Field" columns.')
          return
        }

        const parsed: ParsedRow[] = []
        ws.eachRow((row, rowNum) => {
          if (rowNum === 1) return // skip header
          const rawFieldId  = fieldIdCol ? String(row.getCell(fieldIdCol).value ?? '') : ''
          const rawLabel    = fieldCol   ? String(row.getCell(fieldCol).value ?? '')   : ''
          const rawValue    = valueCol   ? row.getCell(valueCol).value                 : ''
          const value       = rawValue === null || rawValue === undefined ? '' : (typeof rawValue === 'object' ? String(rawValue) : rawValue as string | number)

          if (!sectionCol) void sectionCol // suppress unused warning

          if (rawFieldId && fieldIdMap.has(rawFieldId)) {
            parsed.push({ field: rawLabel || rawFieldId, value, matched: true, fieldId: rawFieldId })
          } else if (rawLabel && fieldMap.has(rawLabel.toLowerCase())) {
            const id = fieldMap.get(rawLabel.toLowerCase())!
            parsed.push({ field: rawLabel, value, matched: true, fieldId: id })
          } else if (rawLabel) {
            parsed.push({ field: rawLabel, value, matched: false })
          }
        })

        if (parsed.length === 0) {
          setImportError('No matching fields found. Make sure the spreadsheet was exported from CapitalMarketOS.')
          return
        }

        setImportData(parsed)
        setShowPreview(true)
      } catch (err) {
        setImportError(`Failed to parse spreadsheet: ${err instanceof Error ? err.message : 'Unknown error'}`)
      }
    }
    reader.readAsArrayBuffer(file)

    // Reset input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleConfirmImport = () => {
    if (!importData) return
    const newValues: Record<string, number | string> = {}
    importData
      .filter((r) => r.matched && r.fieldId)
      .forEach((r) => {
        const val = r.value
        const num = typeof val === 'string' ? parseFloat(val) : val
        newValues[r.fieldId!] = isNaN(num as number) ? val : num
      })
    onImport(newValues)
    setShowPreview(false)
    setImportData(null)
  }

  const matchedCount   = importData?.filter((r) => r.matched).length  ?? 0
  const unmatchedCount = importData?.filter((r) => !r.matched).length ?? 0

  return (
    <div className="space-y-3">
      {/* Action Buttons */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleExport}
          className="flex items-center gap-2 rounded-lg border border-amber-400/20 bg-amber-400/[0.06] px-4 py-2 text-[11px] font-medium text-amber-300 hover:bg-amber-400/10 transition-colors"
        >
          <Download size={13} />
          Export to Spreadsheet
        </button>

        <label className="flex items-center gap-2 rounded-lg border border-emerald-400/20 bg-emerald-400/[0.06] px-4 py-2 text-[11px] font-medium text-emerald-300 hover:bg-emerald-400/10 transition-colors cursor-pointer">
          <Upload size={13} />
          Import from Spreadsheet
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileSelect}
            className="hidden"
          />
        </label>

        <span className="text-[10px] text-stone-600">
          <FileSpreadsheet size={11} className="inline mr-1" />
          Supports .xlsx, .xls
        </span>
      </div>

      {/* Error */}
      {importError && (
        <div className="flex items-center gap-2 rounded-lg border border-red-400/20 bg-red-400/[0.05] px-3 py-2">
          <AlertTriangle size={13} className="text-red-400 flex-shrink-0" />
          <span className="text-[11px] text-red-300">{importError}</span>
          <button onClick={() => setImportError(null)} className="ml-auto text-stone-500 hover:text-stone-300">
            <X size={12} />
          </button>
        </div>
      )}

      {/* Import Preview */}
      {showPreview && importData && (
        <div className="rounded-lg border border-amber-400/15 bg-black/40 backdrop-blur-xl">
          <div className="flex items-center justify-between border-b border-amber-400/10 px-4 py-2.5">
            <div className="flex items-center gap-2">
              <Table size={14} className="text-amber-400" />
              <span className="text-[12px] font-semibold text-stone-100">Import Preview</span>
              <span className="text-[10px] text-stone-500">
                {matchedCount} matched, {unmatchedCount} unmatched
              </span>
            </div>
            <button onClick={() => { setShowPreview(false); setImportData(null) }} className="text-stone-500 hover:text-stone-300">
              <X size={14} />
            </button>
          </div>
          <div className="max-h-[300px] overflow-y-auto p-3">
            <table className="dense-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Field</th>
                  <th>Field ID</th>
                  <th className="text-right">Value</th>
                </tr>
              </thead>
              <tbody>
                {importData.map((row, i) => (
                  <tr key={i} className={row.matched ? '' : 'opacity-50'}>
                    <td>
                      {row.matched ? (
                        <CheckCircle2 size={12} className="text-emerald-400" />
                      ) : (
                        <AlertTriangle size={12} className="text-amber-400" />
                      )}
                    </td>
                    <td className="text-stone-300">{row.field}</td>
                    <td className="font-mono text-[10px] text-stone-500">{row.fieldId ?? '—'}</td>
                    <td className="text-right font-mono text-stone-200">{String(row.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-amber-400/10 px-4 py-2.5">
            <button
              onClick={() => { setShowPreview(false); setImportData(null) }}
              className="rounded border border-stone-600/50 px-3 py-1.5 text-[10px] font-medium text-stone-400 hover:text-stone-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmImport}
              className="rounded border border-emerald-400/25 bg-emerald-400/10 px-4 py-1.5 text-[10px] font-medium text-emerald-300 hover:bg-emerald-400/20 transition-colors"
            >
              Import {matchedCount} Fields
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
